import { FC, useEffect } from "react";

import { FormType, getDonationOrTicketingFormLink, TicketingFormCategory } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { AmplitudeEvents } from "../../../../../common/constants/amplitude";
import { useCurrentUserContext } from "../../../../../common/contexts/CurrentUserContext";
import { useLocaleContext } from "../../../../../common/contexts/LocaleContext";
import { useIsOnboarding } from "../../../../../common/hooks/useIsOnboarding";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { LiveFormActionFooterPortal } from "../../common/LiveFormEditorActionFooter";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";

import { StepReviewStateless } from "./StepReview.stateless";
import { useActivateTicketing } from "./useActivateTicketing";

import { FROM_ONBOARDING_QUERY_PARAM } from "@/common/constants/url";
import { useUpdateTicketingDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";

const getStepReviewTitleTranslationKey = (formCategory: TicketingFormCategory) => {
  switch (formCategory) {
    case TicketingFormCategory.Shop:
      return "editor.stepReview.shop.title";
    case TicketingFormCategory.Lottery:
      return "editor.stepReview.lottery.title";
    case TicketingFormCategory.Sweepstakes:
      return "editor.stepReview.sweepstakes.title";
    case TicketingFormCategory.Auction:
      return "editor.stepReview.auction.title";
    case TicketingFormCategory.MembershipV2:
      return "editor.stepReview.membership.title";
    case TicketingFormCategory.Custom:
      return "editor.stepReview.custom.title";
    default:
      return "editor.stepReview.title";
  }
};

export const TicketingLiveEditorStepReview: FC = () => {
  const { t } = useTranslate();
  const {
    setEditOrCreateMode,
    goToPreviousStep,
    apiFormObject: apiTicketingForm,
    shareFormModalState,
    formCategory,
    setIsNavigationOpen,
  } = useLiveFormEditorContext<"ticketing">();
  const { isoLocale } = useLocaleContext();
  const { firstPaymentDate } = useCurrentUserContext();

  const { isOnboarding } = useIsOnboarding();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  // Always set to edit mode on this step
  useEffect(() => {
    setIsNavigationOpen(false);
    setEditOrCreateMode("edit");
  }, [setEditOrCreateMode, setIsNavigationOpen]);

  const formLink = getDonationOrTicketingFormLink({
    form: {
      id: apiTicketingForm.id,
      path: apiTicketingForm.path,
      formCategory: apiTicketingForm.formCategory,
      formType: FormType.Ticketing,
      isPrimaryCampaign: undefined,
      isPrimaryTeam: undefined,
    },
    isoLocale,
  });

  const formLinkWithContext = isOnboarding
    ? `${formLink}${formLink.includes("?") ? "&" : "?"}${FROM_ONBOARDING_QUERY_PARAM}=true`
    : formLink;

  useActivateTicketing();

  const titleTranslation = getStepReviewTitleTranslationKey(formCategory);

  useUpdateTicketingDataInPreview({
    form: useForm(),
    mutate: () => {},
  });

  return (
    <>
      <LiveFormActionFooterPortal
        onBack={() => {
          logUserActionEvent(AmplitudeEvents.FormBackButtonClicked);
          goToPreviousStep();
        }}
      />
      <StepReviewStateless
        title={t("dashboard", titleTranslation)}
        description={t("dashboard", `editor.stepReview.description`)}
        isOnboarding={isOnboarding}
        formLink={formLinkWithContext}
        showCommunicationCard={Boolean(firstPaymentDate && formCategory === TicketingFormCategory.Event)}
        formId={apiTicketingForm.id}
        formStatus={apiTicketingForm.status}
        onShareForm={shareFormModalState.openModal}
        onLogUserAction={logUserActionEvent}
        onNavigate={(path) => Promise.resolve(window.open(path, "_blank"))}
        formCategory={formCategory}
        communicationTexts={{
          title: t("dashboard", "setupEventCommunications.title"),
          description: t("dashboard", "setupEventCommunications.description"),
        }}
      />
    </>
  );
};
